import React from 'react';
import { StyleSheet, View } from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import Icon, { IconProps } from 'shared/components/vxl/icon/Icon/Icon';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import theme from 'shared/styles/theme';

type TrailingItemType = 'icon' | 'link';
export interface SectionDividerProps {
  labelText: string;
  /** item type used to determine the props being passed in.  */
  trailingItemType?: TrailingItemType;
  /**
   * The trailing item can be either an ```Icon``` component or a ```SectionDividerLink```
   */
  trailingItem?: IconProps | SectionDividerLinkProps;
  /** background color for the section divider. */
  background?: string;
  /** text size for the section divider label. */
  textSize?: 'small' | 'large';
}

interface TrailingItemProps {
  type: TrailingItemType;
  item: IconProps | SectionDividerLinkProps | undefined;
}

export interface SectionDividerLinkProps {
  linkText?: string;
  onButtonPress?: () => void;
}

const TrailingItem = ({ type, item }: TrailingItemProps) => {
  const TrailingItemType = { ICON: 'icon', LINK: 'link' };
  if (type === TrailingItemType.ICON) {
    return <Icon {...(item as IconProps)} />;
  }
  if (type === TrailingItemType.LINK) {
    const linkProps = item as SectionDividerLinkProps;
    return (
      <PressableWithDelay
        onPress={linkProps.onButtonPress!}
        style={styles.trailingPressable}
      >
        <HyperlinkSmText numberOfLines={2} style={styles.linkText}>
          {linkProps.linkText}
        </HyperlinkSmText>
      </PressableWithDelay>
    );
  }
};

const SectionDivider = ({
  labelText,
  trailingItemType,
  trailingItem,
  background = theme.color.background.primary,
  textSize = 'small',
}: SectionDividerProps) => {
  return (
    <View
      style={[
        styles.container,
        background === 'none' ? undefined : { backgroundColor: background },
      ]}
    >
      {textSize === 'small' ? (
        <BodySmBoldText numberOfLines={2} style={styles.labelText}>
          {labelText}
        </BodySmBoldText>
      ) : (
        <TitleSmText numberOfLines={2} style={styles.labelText}>
          {labelText}
        </TitleSmText>
      )}
      {trailingItemType ? (
        <TrailingItem type={trailingItemType} item={trailingItem} />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'flex-end',
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: 36,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  labelText: {
    color: theme.color.text.primary,
    flex: 1,
    flexWrap: 'wrap',
    marginRight: 6,
    textAlign: 'left',
  },
  linkText: {
    color: theme.color.interactive.primary,
    textAlign: 'right',
  },
  trailingPressable: {
    flex: 1,
  },
});

export default SectionDivider;
